import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  NumberInput,
  NumberInputField,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import { FaEdit, FaPlus } from 'react-icons/fa';

import PaginationControls from 'components/table/PaginationControls';

import { useColors } from './colors';
import UpdateSupplierShippingModal from 'features/suppliers/components/UpdateSupplierShippingModal';

import {
  getSupplierShippingPricesApi,
  upsertSupplierShippingPriceApi,
} from 'features/suppliers/suppliers.api';

const PAGE_LIMIT = 50;

const ShippingPricesModal = ({ isOpen, onClose, supplierId, supplierName }) => {
  const dispatch = useDispatch();
  const {
    textColor,
    secondaryTextColor,
    backgroundCardColor,
    borderColor,
    inputColor,
  } = useColors();

  const { getShippingPricesStatus, shippingPricesPage, upsertShippingPriceStatus } =
    useSelector((state) => state.suppliers);

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [newSku, setNewSku] = useState('');
  const [newCost, setNewCost] = useState('');
  const [editTarget, setEditTarget] = useState(null);

  const fetchPrices = useCallback(() => {
    if (!supplierId) return;
    dispatch(
      getSupplierShippingPricesApi({
        id: supplierId,
        page,
        limit: PAGE_LIMIT,
        search,
      }),
    );
  }, [dispatch, supplierId, page, search]);

  useEffect(() => {
    if (isOpen && supplierId) {
      setPage(1);
      setSearch('');
      setSearchInput('');
      setNewSku('');
      setNewCost('');
      setEditTarget(null);
    }
  }, [isOpen, supplierId]);

  useEffect(() => {
    if (isOpen && supplierId) {
      fetchPrices();
    }
  }, [isOpen, supplierId, fetchPrices]);

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const inventoryIncludesShipping = shippingPricesPage?.inventoryIncludesShipping !== false;
  const items = shippingPricesPage?.items || [];
  const totalPages = shippingPricesPage?.totalPages || 1;
  const totalCount = shippingPricesPage?.totalCount ?? 0;

  const handleAddNew = async () => {
    const sku = newSku.trim();
    const cost = Number(newCost);
    if (!sku || !Number.isFinite(cost) || cost < 0) return;

    const result = await dispatch(
      upsertSupplierShippingPriceApi({ id: supplierId, sku, cost }),
    );
    if (upsertSupplierShippingPriceApi.fulfilled.match(result) && result.payload?.success) {
      setNewSku('');
      setNewCost('');
      fetchPrices();
    }
  };

  const handleEditSaved = () => {
    setEditTarget(null);
    fetchPrices();
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent
          maxW="min(1100px, 96vw)"
          maxH="90vh"
          borderRadius="xl"
          bg={backgroundCardColor}
        >
          <ModalHeader
            color={textColor}
            borderBottomWidth="1px"
            borderColor={borderColor}
            pb={3}
          >
            <Text fontSize="xl" fontWeight="600">
              Shipping prices
            </Text>
            <Text fontSize="sm" fontWeight="normal" color={secondaryTextColor}>
              {supplierName || 'Supplier'} · {totalCount} SKU{totalCount === 1 ? '' : 's'}
            </Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py={4} overflowY="auto">
            <VStack align="stretch" spacing={4}>
              {inventoryIncludesShipping && (
                <Alert status="info" borderRadius="md" fontSize="sm">
                  <AlertIcon />
                  This supplier includes shipping in inventory. External shipping prices are not
                  used for costing.
                </Alert>
              )}

              {!inventoryIncludesShipping && (
                <Box
                  p={4}
                  borderWidth="1px"
                  borderColor={borderColor}
                  borderRadius="md"
                >
                  <Text fontSize="sm" fontWeight="semibold" color={textColor} mb={3}>
                    Add SKU
                  </Text>
                  <HStack spacing={3} align="flex-end" flexWrap="wrap">
                    <FormControl maxW="280px" flex="1" minW="160px">
                      <FormLabel fontSize="xs" color={secondaryTextColor}>
                        SKU
                      </FormLabel>
                      <Input
                        size="sm"
                        value={newSku}
                        onChange={(e) => setNewSku(e.target.value)}
                        placeholder="e.g. SAMPLE-SKU-001"
                        color={textColor}
                        bg={inputColor}
                        borderColor={borderColor}
                      />
                    </FormControl>
                    <FormControl maxW="140px">
                      <FormLabel fontSize="xs" color={secondaryTextColor}>
                        Cost ($)
                      </FormLabel>
                      <NumberInput
                        min={0}
                        precision={2}
                        size="sm"
                        value={newCost}
                        onChange={(_, v) => setNewCost(v)}
                      >
                        <NumberInputField
                          color={textColor}
                          bg={inputColor}
                          borderColor={borderColor}
                        />
                      </NumberInput>
                    </FormControl>
                    <Button
                      leftIcon={<FaPlus />}
                      colorScheme="blue"
                      size="sm"
                      onClick={handleAddNew}
                      isLoading={upsertShippingPriceStatus === 'pending'}
                      isDisabled={!newSku.trim() || newCost === ''}
                    >
                      Add
                    </Button>
                  </HStack>
                </Box>
              )}

              <InputGroup size="sm" maxW="320px">
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder="Search SKU..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  color={textColor}
                  bg={inputColor}
                  borderColor={borderColor}
                />
              </InputGroup>

              {getShippingPricesStatus === 'pending' && !items.length ? (
                <Flex justify="center" py={10}>
                  <Spinner color="blue.500" />
                </Flex>
              ) : items.length === 0 ? (
                <Text fontSize="sm" color={secondaryTextColor} py={6} textAlign="center">
                  {search ? 'No SKUs match your search.' : 'No shipping prices uploaded yet.'}
                </Text>
              ) : (
                <TableContainer
                  borderWidth="1px"
                  borderColor={borderColor}
                  borderRadius="md"
                  maxH="50vh"
                  overflowY="auto"
                >
                  <Table size="sm" variant="simple">
                    <Thead position="sticky" top={0} bg={backgroundCardColor} zIndex={1}>
                      <Tr>
                        <Th color={secondaryTextColor} fontSize="xs" py={2}>
                          SKU
                        </Th>
                        <Th color={secondaryTextColor} fontSize="xs" py={2} isNumeric>
                          Cost ($)
                        </Th>
                        {!inventoryIncludesShipping && (
                          <Th color={secondaryTextColor} fontSize="xs" py={2} w="60px" />
                        )}
                      </Tr>
                    </Thead>
                    <Tbody>
                      {items.map((row) => (
                        <Tr key={row.id}>
                          <Td fontSize="sm" color={textColor} py={2}>
                            {row.id}
                          </Td>
                          <Td fontSize="sm" color={textColor} py={2} isNumeric>
                            ${Number(row.cost).toFixed(2)}
                          </Td>
                          {!inventoryIncludesShipping && (
                            <Td py={2}>
                              <IconButton
                                aria-label="Edit shipping"
                                icon={<FaEdit />}
                                size="xs"
                                variant="ghost"
                                colorScheme="blue"
                                onClick={() =>
                                  setEditTarget({
                                    sku: row.id,
                                    cost: row.cost,
                                  })
                                }
                              />
                            </Td>
                          )}
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </TableContainer>
              )}

              {totalPages > 1 && (
                <PaginationControls
                  currentPage={page}
                  totalPages={totalPages}
                  onPageChange={setPage}
                />
              )}
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>

      <UpdateSupplierShippingModal
        isOpen={!!editTarget}
        onClose={() => setEditTarget(null)}
        supplierId={supplierId}
        supplierName={supplierName}
        sku={editTarget?.sku}
        initialCost={editTarget?.cost}
        inventoryIncludesShipping={inventoryIncludesShipping}
        onSaved={handleEditSaved}
        textColor={textColor}
        secondaryTextColor={secondaryTextColor}
        borderColor={borderColor}
      />
    </>
  );
};

export default ShippingPricesModal;
